import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { NameCheck } from './NameCheck';
import { RegistrationForm } from './RegistrationForm';
import { MemberConfirmation } from './MemberConfirmation';
import { PaymentInstructions } from './PaymentInstructions';
import { savePendingRegistration, completeRegistration } from '../../lib/payment/paymentHandler';
import { Player } from '../../types';
import { useRegistration } from '../../contexts/RegistrationContext';
import { sendRegistrationEmail } from '../../lib/email/registrationEmail';

interface CostConfig {
  member_cost: number;
  non_member_cost: number;
  late_fee: number;
  registration_deadline: string | null;
  start_date: string;
}

interface MembershipFees {
  student_fee: number;
  single_fee: number;
  family_fee: number;
}

interface RegistrationModuleProps {
  onComplete?: (memberId: string, isNewMember: boolean) => void;
  onCancel?: () => void;
  selectedTournament: Tournament;
  onPlayersImported?: (players: Player[]) => void; 
  initialPlayers?: Player[]; 
}

export function RegistrationModule({ 
  onComplete, 
  onCancel,
  selectedTournament,
  onPlayersImported,
  initialPlayers = []
}: RegistrationModuleProps) {
  const [step, setStep] = useState<'name-check' | 'registration' | 'confirmation' | 'payment'>('name-check');
  const [existingMember, setExistingMember] = useState<any>(null);
  const [registeredMember, setRegisteredMember] = useState<any>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const { selectedTournamentId, completeRegistration } = useRegistration();
  const [costConfig, setCostConfig] = useState<CostConfig | null>(null);
  const [membershipFees, setMembershipFees] = useState<MembershipFees | null>(null);

  // Validate tournament ID
  useEffect(() => {
    if (!selectedTournamentId) {
      console.warn('Missing tournament ID in RegistrationModule');
    }
  }, [selectedTournamentId]);

  // Load cost configuration and membership fees
  useEffect(() => {
    const loadCosts = async () => {
      try {
        if (!selectedTournamentId) {
          throw new Error('Missing tournament ID');
        }

        // Get tournament costs
        const { data: costData, error: costError } = await supabase
          .from('tournament_costs')
          .select('member_cost, non_member_cost, late_fee')
          .eq('tournament_id', selectedTournamentId)
          .maybeSingle(); // Use maybeSingle instead of single to handle no rows

        if (costError && !costError.message?.includes('No rows found')) {
          throw costError;
        }

        // Get tournament registration deadline
        const { data: tournament, error: tournamentError } = await supabase
          .from('tournaments')
          .select('registration_deadline, start_date, metadata')
          .eq('id', selectedTournamentId)
          .single();

        if (tournamentError) throw tournamentError;

        // Get membership fees
        const { data: fees, error: feesError } = await supabase
          .from('membership_fees')
          .select('student_fee, single_fee, family_fee')
          .eq('season', '2025')
          .single();

        if (feesError) throw feesError;

        console.log('Loaded costs:', {
          costData,
          tournament,
          fees
        });

        // If no costs in tournament_costs table, try to get from metadata
        const costs = costData || {
          member_cost: tournament.metadata?.cost?.member?.amount || 15000,
          non_member_cost: tournament.metadata?.cost?.nonMember?.amount || 20000,
          late_fee: tournament.metadata?.cost?.lateRegistration?.amount || 2000
        };

        // Combine cost data
        setCostConfig({
          member_cost: costs.member_cost,
          non_member_cost: costs.non_member_cost,
          late_fee: costs.late_fee,
          registration_deadline: tournament.registration_deadline,
          start_date: tournament.start_date
        });

        setMembershipFees(fees);
      } catch (error) {
        console.error('Error loading costs:', error);
        setRegistrationError('Failed to load registration costs');
      }
    };

    if (selectedTournamentId) {
      loadCosts();
    }
  }, [selectedTournamentId]);

  const handleCancel = () => {
    setStep('name-check');
    setExistingMember(null);
    setRegistrationError(null);
    if (onCancel) {
      onCancel();
    }
  };

  const handleNameCheck = async (member: { id: string; name: string }) => {
    try {
      // Check registration deadline if tournament is selected
      if (selectedTournament) {
        // Check if registration is still open
        const { data: isOpen, error: deadlineError } = await supabase
          .rpc('is_registration_open', { p_tournament_id: selectedTournament.id });

        if (deadlineError) throw deadlineError;
        if (!isOpen) {
          throw new Error('Registration is closed for this tournament');
        }
      }

      if (member.id) {
        // For existing members
        const { data, error } = await supabase
          .from('members')
          .select('id, name, email, current_status, season')
          .eq('id', member.id)
          .maybeSingle();

        if (error && !error.code?.includes('PGRST116')) {
          throw error;
        }

        if (data) {
          setExistingMember(data);
          setStep('confirmation');
        } else {
          // Member not found, proceed with registration
          setStep('registration');
        }
      } else {
        // New member registration
        setStep('registration');
      }
    } catch (error) {
      if (error instanceof Error) {
        setRegistrationError(`Failed to check member name: ${error.message}`);
      } else {
        setRegistrationError('Failed to check member name');
      }
    }
  };

  const handleRegistration = async (formData: {
    name: string;
    email: string;
    phone?: string;
    join2025: boolean;
    membershipType: 'student' | 'single' | 'family' | null;
    isVietnamese: boolean;
    notes: string;
  }) => {
    try {
      // Check registration deadline if tournament is selected
      if (selectedTournament) {
        const now = new Date();
        const { data: tournament } = await supabase
          .from('tournaments')
          .select('registration_deadline, start_date')
          .eq('id', selectedTournamentId)
          .single();

        const deadline = selectedTournament.registrationDeadline 
          ? (() => {
              const d = new Date(selectedTournament.registrationDeadline);
              d.setUTCHours(23, 59, 59, 999);
              return d;
            })()
          : new Date(selectedTournament.startDate);
        
        if (now > deadline) {
          throw new Error('Registration is closed for this tournament');
        }
      }

      // Store registration data temporarily
      const { data: newMember, error: memberError } = await supabase
        .from('members')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          membership_status: formData.join2025 ? 'active' : 'inactive',
          current_status: formData.join2025 && formData.isVietnamese ? 'M' : 
                         formData.join2025 ? 'NVM' : 
                         'inactive',
          season: {
            '2025': {
              status: formData.join2025 && formData.isVietnamese ? 'M' : 
                      formData.join2025 ? 'NVM' : 
                      'inactive',
              flight: 'A',
              handicap: 0.0
            }
          }
        }])
        .select()
        .single();

      if (memberError) throw memberError;

      setRegisteredMember({
        ...newMember,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        join2025: formData.join2025,
        membershipType: formData.membershipType,
        isVietnamese: formData.isVietnamese,
        notes: formData.notes,
        isNewMember: true
      });
      setStep('payment');
    } catch (error) {
      console.error('Error registering member:', error);
      setRegistrationError('Failed to register member');
    }
  };

  const handleConfirmation = async (memberId: string, data: { 
    join2025: boolean; 
    isVietnamese: boolean;
    membershipType: 'student' | 'single' | 'family' | null;
    notes: string;
    email: string;
  }) => {
    try {
      console.log('Processing confirmation:', {
        memberId,
        data,
        existingMember
      });

      // Update member email if changed
      if (data.email !== existingMember.email) {
        const { error: emailUpdateError } = await supabase
          .from('members')
          .update({ email: data.email })
          .eq('id', memberId);

        if (emailUpdateError) throw emailUpdateError;
      }

      setRegisteredMember({
        ...existingMember,
        email: data.email,
        join2025: data.join2025,
        membershipType: data.membershipType,
        isVietnamese: data.isVietnamese,
        notes: data.notes
      });
      
      console.log('Setting registered member:', {
        ...existingMember,
        join2025: data.join2025,
        membershipType: data.membershipType,
        isVietnamese: data.isVietnamese,
        notes: data.notes
      });

      setStep('payment');
    } catch (error) {
      console.error('Error updating member:', error);
      setRegistrationError('Failed to update member status');
    }
  };

  const handlePaymentComplete = async () => {
    try {
      // Complete registration and add player to tournament
      const result = await completeRegistration();
      if (!result) {
        throw new Error('No pending registration found');
      }

      // Update member status after successful payment
      if (registeredMember?.isNewMember) {
        // Create new member after payment
        const { data: member, error: memberError } = await supabase
          .from('members')
          .insert([{
            name: registeredMember.name,
            email: registeredMember.email,
            phone: registeredMember.phone,
            membership_status: registeredMember.join2025 ? 'active' : 'inactive',
            current_status: registeredMember.join2025 && registeredMember.isVietnamese ? 'M' : 
                          registeredMember.join2025 ? 'NVM' : 
                          'inactive',
            season: {
              '2025': {
                status: registeredMember.join2025 && registeredMember.isVietnamese ? 'M' : 
                        registeredMember.join2025 ? 'NVM' : 
                        'inactive',
                membershipType: registeredMember.join2025 ? registeredMember.membershipType : null,
                flight: 'A',
                handicap: 0.0
              }
            }
          }])
          .select()
          .single();

        if (memberError) throw memberError;
        
        // Update registeredMember with actual member data
        setRegisteredMember(member);
      } else if (registeredMember) {
        // Update existing member status
        const currentStatus = registeredMember.join2025 && registeredMember.isVietnamese ? 'M' : 
                            registeredMember.join2025 ? 'NVM' : 
                            'inactive';
        
        const membershipStatus = registeredMember.join2025 ? 'active' : 'inactive';

        const { error: updateError } = await supabase
          .from('members')
          .update({
            membership_status: membershipStatus,
            current_status: currentStatus,
            season: {
              ...registeredMember.season,
              '2025': {
                status: currentStatus,
                membershipType: registeredMember.join2025 ? registeredMember.membershipType : null,
                flight: 'A',
                handicap: 0.0
              }
            }
          })
          .eq('id', registeredMember.id);

        if (updateError) throw updateError;
      }

      // Only add player to tournament after successful payment
      if (result.success && onPlayersImported && registeredMember) {
        console.log('Adding player after payment:', {
          tournamentId: result.tournamentId, 
          memberId: registeredMember.id,
          playerId: registeredMember.id, // Using member ID as player ID
          notes: registeredMember.notes // Log notes for debugging
        });

        const playerInsert = {
          id: registeredMember.id,
          name: registeredMember.name,
          tournament_id: result.tournamentId,
          handicap: registeredMember.season?.['2025']?.handicap || 0,
          flight: registeredMember.season?.['2025']?.flight || 'A',
          division: registeredMember.is_senior ? 'senior' : 'regular',
          notes: registeredMember.notes || '',
          scores: [],
          total_score: 0
        };
        console.log('Creating player with data:', playerInsert);

        const { data: playerData, error: playerError } = await supabase
          .from('players')
          .insert([playerInsert])
          .select()
          .single();

        if (playerError) throw playerError;

        if (playerData) {
          console.log('Player created successfully:', playerData);
          const newPlayer: Player = {
            id: registeredMember.id,
            name: playerData.name,
            tournamentId: playerData.tournament_id,
            handicap: playerData.handicap,
            flight: playerData.flight,
            division: playerData.division,
            notes: registeredMember.notes || '', // Use registeredMember.notes instead of playerData.notes
            scores: playerData.scores || [],
            totalScore: playerData.total_score || 0
          };
          console.log('Adding player to local state:', newPlayer);
          onPlayersImported([...initialPlayers, newPlayer]);

          // Send registration confirmation email
          try {
            console.log('Sending registration confirmation email:', {
              memberId: registeredMember.id,
              memberName: registeredMember.name,
              tournamentId: result.tournamentId,
              tournamentName: selectedTournament.name
            });
            
            const emailResult = await sendRegistrationEmail({
              memberId: registeredMember.id,
              memberName: registeredMember.name,
              tournamentId: result.tournamentId,
              tournamentName: selectedTournament.name
            });

            if (!emailResult.success) {
              console.error('Failed to send registration email:', emailResult.error);
            } else {
              console.log('Registration email sent successfully');
            }
          } catch (emailError) {
            console.error('Error sending registration email:', emailError);
          }
        }
      }

      if (onComplete) {
        onComplete(registeredMember.id, !existingMember);
      }
    } catch (error) {
      console.error('Error completing registration:', error);
      throw error;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-md mx-auto">
      {registrationError && (
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-md">
          {registrationError}
        </div>
      )}

      {step === 'name-check' && (
        <NameCheck onSubmit={handleNameCheck} onCancel={handleCancel} />
      )}

      {step === 'registration' && (
        <RegistrationForm onSubmit={handleRegistration} onCancel={handleCancel} />
      )}

      {step === 'confirmation' && existingMember && (
        <MemberConfirmation 
          member={existingMember}
          onConfirm={handleConfirmation}
          onCancel={handleCancel}
        />
      )}

      {step === 'payment' && registeredMember && (
        <PaymentInstructions
          memberName={registeredMember.name}
          registeredMember={registeredMember}
          tournamentId={selectedTournamentId}
          memberId={registeredMember.id}
          costConfig={costConfig}
          membershipFees={membershipFees}
          onComplete={handlePaymentComplete}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}